import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoProjectOptions, fetchLocalLocations } from '@/data/sources';
import { fetchSeoMapRank } from '@/data/sources-seoc';
import type { MapRankResult, MapRankRow } from '@/types/seoc';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/map-rank — Local Map-Rank tracking (W3a). Read-only, source-attributed
 * local-pack rankings for a keyword + location, from DataForSEO SERP local_pack.
 *
 * HONESTY + SAFETY INVARIANTS (W3a contract):
 *  - PAID (DataForSEO local pack): NEVER auto-fires. Project options + the
 *    location list are free reads; the ranking check runs ONLY on an explicit
 *    click. No useQuery is wired to the paid read, so mounting charges nothing.
 *  - Location keys come from the existing local-locations config (free list).
 *  - Source-attributed (source + provider fetched_at + cache indicator); a null
 *    map position renders as an em-dash — a business that isn't in the pack is
 *    NEVER given a fabricated rank.
 *  - Results render in the shared SortableTable (client-side sort only — free).
 *  - Honest states: idle / loading / error / not-configured (503).
 */

/** Static fallback location keys (used until the config list resolves). */
const FALLBACK_LOCATIONS: { key: string; label: string }[] = [
  { key: 'uk', label: 'United Kingdom (no geo filter)' },
  { key: 'london', label: 'London' },
];

function sourceLabel(source: string): string {
  if (source.startsWith('dataforseo.serp.local_pack')) return 'DataForSEO Local Pack';
  if (source.startsWith('dataforseo.serp')) return 'DataForSEO SERP';
  if (source.startsWith('dataforseo')) return 'DataForSEO';
  return source;
}

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** em-dash for a null position — a business out of the pack is never faked. */
function orPosition(v: number | null): string {
  return v === null ? '—' : formatInt(v);
}

interface Panel {
  title: string;
  body: string;
}

type SectionState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; panel: Panel }
  | { status: 'done'; data: T };

function describeError(err: unknown): Panel {
  if (err instanceof ApiError) {
    if (err.status === 503 || err.code === 'provider_unavailable') {
      return {
        title: 'Map-rank tracking isn’t connected yet',
        body: 'Local map rankings need the DataForSEO local-pack connection, which isn’t configured for this environment yet. Nothing was fetched and nothing was charged — an honest “not configured” state, never placeholder data.',
      };
    }
    if (err.status === 402 || err.code === 'budget_exceeded') {
      return {
        title: 'Monthly provider budget reached',
        body: 'This check would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. It resumes when the budget resets, or after you raise the cap on Spend & Budget.',
      };
    }
    if (err.status === 429 || err.code === 'rate_limited') {
      return {
        title: 'Provider rate limit reached',
        body: 'This workspace has hit the provider rate limit, so nothing was fetched or charged. Try again as the window rolls over.',
      };
    }
    if (err.status === 502 || err.code === 'provider_error') {
      return {
        title: 'The provider request failed',
        body: `${err.message ? `${err.message} ` : ''}Nothing was stored or charged — try again in a moment.`,
      };
    }
    if (err.status === 400) {
      return {
        title: 'A keyword and location are required',
        body: 'Enter a keyword and pick a location before running the check. Nothing was fetched or charged.',
      };
    }
    if (err.status === 404) {
      return {
        title: 'Project not found',
        body: 'This SEO project isn’t available to your workspace. Pick another project and try again.',
      };
    }
    return {
      title: 'The check could not be completed',
      body: err.message || 'The data service returned an unexpected response. Nothing was charged — try again.',
    };
  }
  return {
    title: 'The check could not be completed',
    body: err instanceof Error ? err.message : 'Unexpected error — nothing was charged. Try again.',
  };
}

function SourceFooter({
  source,
  fetchedAt,
  cacheHit,
}: {
  source: string;
  fetchedAt: string | null;
  cacheHit: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
      <Badge tone="neutral">Source: {sourceLabel(source)}</Badge>
      <Badge tone={cacheHit ? 'neutral' : 'success'}>
        {cacheHit ? 'Served from cache' : 'Live result'}
      </Badge>
      <span>{fetchedAt ? `Last updated ${fetchedAt}` : 'Update time not reported'}</span>
    </div>
  );
}

const RANKING_COLUMNS: SortableColumn<MapRankRow>[] = [
  {
    key: 'map_position',
    header: 'Map pos',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (r) => r.map_position,
    render: (r) => orPosition(r.map_position),
    cellClassName: 'font-mono text-text-primary',
  },
  {
    key: 'business_name',
    header: 'Business',
    sortable: true,
    sortValue: (r) => r.business_name,
    render: (r) => r.business_name,
    truncate: true,
    maxWidthClass: 'max-w-[280px]',
    title: (r) => r.business_name,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'keyword',
    header: 'Keyword',
    sortable: true,
    sortValue: (r) => r.keyword,
    render: (r) => r.keyword,
    truncate: true,
    maxWidthClass: 'max-w-[220px]',
    title: (r) => r.keyword,
    cellClassName: 'text-text-secondary',
  },
  {
    key: 'location',
    header: 'Location',
    sortable: true,
    sortValue: (r) => r.location,
    render: (r) => r.location,
    cellClassName: 'text-text-tertiary',
  },
  {
    key: 'checked_at',
    header: 'Checked',
    sortable: true,
    sortValue: (r) => (r.checked_at === '' ? null : r.checked_at),
    render: (r) => (r.checked_at === '' ? '—' : r.checked_at),
    cellClassName: 'font-mono text-text-secondary',
  },
];

export function SeoMapRankPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const navigate = useNavigate();

  // Free list reads — safe on mount. The paid map-rank check is never wired to a
  // query; it runs on click only.
  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const locationsQ = useQuery({
    queryKey: ['seo', 'local-locations'],
    queryFn: fetchLocalLocations,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();
  const locations = locationsQ.data && locationsQ.data.length > 0 ? locationsQ.data : FALLBACK_LOCATIONS;

  const projectId = resolveActiveProjectId(active, projects);

  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState<string | null>(null);
  const locationKey =
    location !== null && locations.some((l) => l.key === location)
      ? location
      : (locations[0]?.key ?? 'uk');

  const [rank, setRank] = useState<SectionState<MapRankResult>>({ status: 'idle' });

  const runCheck = async () => {
    if (projectId === null || rank.status === 'loading') return;
    const trimmed = keyword.trim();
    if (trimmed === '') return;
    setRank({ status: 'loading' });
    try {
      const data = await fetchSeoMapRank(projectId, trimmed, locationKey);
      setRank({ status: 'done', data });
    } catch (err) {
      setRank({ status: 'error', panel: describeError(err) });
    }
  };

  const onProjectChange = (id: string) => {
    void select(Number(id));
    setRank({ status: 'idle' });
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Map Rank" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Map Rank" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Map Rank" />
        <EmptyState
          title="No project to track yet"
          teach="Map-rank tracking runs against an SEO project. Add your first domain on the Projects page, then come back here to see where you rank in the local map pack."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const inputClass =
    'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => onProjectChange(e.target.value)}
        className={inputClass}
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Map Rank"
        subtitle="Where your business ranks in the local map pack for a keyword + location, from licensed DataForSEO local-pack data — loaded on demand, source-attributed, and honest when you’re not in the pack."
        actions={projectSelector}
      />

      {!canManage && (
        <div role="status" className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          Map-rank tracking uses paid provider data, so it’s limited to workspace owners and operators.
        </div>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">DataForSEO Local Pack</Badge>
          <span className="text-sm font-medium text-text-primary">Local map-pack rankings</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-text-tertiary">Paid · budget-guarded · 7-day cache</span>
          </div>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Enter a keyword and pick a location to see the local map pack and where you sit in it.
          Runs only when you press the button — nothing is fetched or charged on load.
        </p>

        <form
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void runCheck();
          }}
        >
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="e.g. pilates studio near me"
            aria-label="Keyword"
            className={`${inputClass} min-w-[14rem] flex-1`}
            disabled={!canManage}
          />
          <label className="flex items-center gap-2">
            <span className="sr-only">Location</span>
            <select
              value={locationKey}
              onChange={(e) => setLocation(e.target.value)}
              className={inputClass}
              aria-label="Map location"
              disabled={!canManage}
            >
              {locations.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={!canManage || rank.status === 'loading' || keyword.trim() === ''}
            title={canManage ? undefined : 'Owner/operator only — this uses paid provider data'}
          >
            {rank.status === 'loading'
              ? 'Checking…'
              : rank.status === 'done'
                ? 'Refresh'
                : 'Check map rank'}
          </Button>
        </form>

        <div className="mt-4">
          {rank.status === 'idle' && (
            <p className="text-sm text-text-tertiary">
              No data yet — enter a keyword and check the local map pack.
            </p>
          )}
          {rank.status === 'loading' && <LoadingState rows={4} />}
          {rank.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{rank.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{rank.panel.body}</p>
            </div>
          )}
          {rank.status === 'done' &&
            (rank.data.rankings.length === 0 ? (
              <div className="rounded-md border border-border-subtle bg-canvas p-3">
                <p className="text-sm text-text-secondary">
                  The provider returned no local-pack results for this query — an honest empty
                  result, not an error.
                </p>
                <div className="mt-2">
                  <SourceFooter
                    source={rank.data.source}
                    fetchedAt={rank.data.fetched_at}
                    cacheHit={rank.data.cache_hit}
                  />
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                <SortableTable
                  rows={rank.data.rankings}
                  columns={RANKING_COLUMNS}
                  getRowKey={(r, i) => `${r.business_name}-${i}`}
                  minWidthClass="min-w-[760px]"
                  caption="Local map-pack rankings by position"
                />
                <SourceFooter
                  source={rank.data.source}
                  fetchedAt={rank.data.fetched_at}
                  cacheHit={rank.data.cache_hit}
                />
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}
